import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { Trash2, Minus, Plus, ShoppingBag, ArrowLeft } from 'lucide-react';

function groupItemsByVendor(items: any[]): Record<string, any[]> {
  const groups: Record<string, any[]> = {};
  for (const item of items) {
    const vendorName = item.metadata?.vendor_name || 'Other';
    if (!groups[vendorName]) groups[vendorName] = [];
    groups[vendorName].push(item);
  }
  return groups;
}

export function CartPage() {
  const { cart, loading, updateItem, removeItem, clearCart } = useCart();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Loading cart...</p>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div style={{
        padding: '60px 20px',
        textAlign: 'center',
        maxWidth: '600px',
        margin: '0 auto',
      }}>
        <ShoppingBag size={64} style={{ color: '#ccc', marginBottom: '20px' }} />
        <h1 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>Your cart is empty</h1>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          Browse our vendors and add some products!
        </p>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            backgroundColor: '#2c3e50',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: 'inherit',
          }}
        >
          <ArrowLeft size={18} />
          Continue Browsing
        </button>
      </div>
    );
  }

  const vendorGroups = groupItemsByVendor(cart.items);
  const vendorNames = Object.keys(vendorGroups);
  // Medusa v2 stores amounts in major currency units (dollars), not cents
  const subtotal = cart.subtotal || 0;
  const total = cart.total || 0;

  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
    }}>
      <h1 style={{ fontSize: '1.8rem', marginBottom: '30px' }}>Your Cart</h1>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {vendorNames.map((vendorName) => {
          const items = vendorGroups[vendorName];
          const vendorSubtotal = items.reduce(
            (sum: number, item: any) => sum + (item.unit_price * item.quantity),
            0
          );

          return (
            <div key={vendorName}>
              {vendorNames.length > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px',
                  paddingBottom: '8px',
                  borderBottom: '2px solid #2c3e50',
                }}>
                  <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#2c3e50' }}>
                    {vendorName}
                  </h2>
                  <span style={{ fontSize: '0.9rem', color: '#666' }}>
                    Subtotal: ${vendorSubtotal.toFixed(2)}
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {items.map((item: any) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    {item.thumbnail && (
                      <img
                        src={item.thumbnail}
                        alt={item.title}
                        style={{
                          width: '80px',
                          height: '80px',
                          objectFit: 'cover',
                          borderRadius: '6px',
                        }}
                      />
                    )}

                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 4px 0', fontSize: '1rem' }}>{item.title}</h3>
                      <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                        ${item.unit_price.toFixed(2)} each
                      </p>
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}>
                      <button
                        onClick={() => {
                          if (item.quantity <= 1) {
                            removeItem(item.id);
                          } else {
                            updateItem(item.id, item.quantity - 1);
                          }
                        }}
                        style={{
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: '#fff',
                          cursor: 'pointer',
                        }}
                      >
                        <Minus size={14} />
                      </button>

                      <span style={{ minWidth: '24px', textAlign: 'center', fontWeight: '600' }}>
                        {item.quantity}
                      </span>

                      <button
                        onClick={() => updateItem(item.id, item.quantity + 1)}
                        style={{
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: '#fff',
                          cursor: 'pointer',
                        }}
                      >
                        <Plus size={14} />
                      </button>
                    </div>

                    <p style={{
                      fontWeight: '600',
                      fontSize: '1.1rem',
                      minWidth: '80px',
                      textAlign: 'right',
                      margin: 0,
                    }}>
                      ${(item.unit_price * item.quantity).toFixed(2)}
                    </p>

                    <button
                      onClick={() => removeItem(item.id)}
                      style={{
                        padding: '8px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        color: '#999',
                      }}
                      aria-label="Remove item"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '10px',
          fontSize: '1rem',
        }}>
          <span>Subtotal</span>
          <span>${subtotal.toFixed(2)}</span>
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontWeight: '700',
          fontSize: '1.2rem',
          borderTop: '1px solid #ddd',
          paddingTop: '10px',
        }}>
          <span>Total</span>
          <span>${total.toFixed(2)}</span>
        </div>

        <Link
          to="/checkout"
          style={{
            display: 'block',
            width: '100%',
            padding: '14px',
            marginTop: '20px',
            backgroundColor: '#2c3e50',
            color: '#fff',
            textAlign: 'center',
            textDecoration: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '1.1rem',
            boxSizing: 'border-box',
          }}
        >
          Proceed to Checkout
        </Link>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '16px',
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: '#666',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.9rem',
            padding: 0,
          }}
        >
          <ArrowLeft size={16} />
          Continue Shopping
        </button>
        <button
          onClick={() => { clearCart(); window.location.href = '/'; }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            backgroundColor: '#fff',
            color: '#999',
            cursor: 'pointer',
            fontSize: '0.85rem',
          }}
        >
          <Trash2 size={14} />
          Clear Cart
        </button>
      </div>
    </div>
  );
}
